interface WorkExperience {
  start_date: string | null
  end_date?: string | null
}

interface ExperienceInterval {
  start: Date
  end: Date
}

/**
 * 경력 기간을 계산합니다. 겹치는 기간은 중복 계산하지 않고 병합하여 계산합니다.
 * 
 * @param experiences 경력 정보 배열
 * @returns { years: number, months: number } 총 경력 (년, 월)
 * 
 * @example
 * 카카오: 2018-06-01 ~ 2019-12-31
 * 글로벌 IT: 2019-06-01 ~ 현재 (겹침)
 * 네이버: 2020-01-01 ~ 2022-12-31
 * 
 * 결과: 2018-06-01 ~ 현재까지의 실제 경력만 계산 (중복 제거)
 */
export const calculateTotalExperience = (experiences: WorkExperience[]) => {
  if (!experiences || experiences.length === 0) {
    return { years: 0, months: 0 }
  }

  const today = new Date()

  // 1. 날짜 순으로 정렬 (시작일 기준 오름차순)
  const sorted: ExperienceInterval[] = experiences
    .filter((exp) => exp.start_date != null) // null인 항목 제외
    .map((exp) => {
      const start = new Date(exp.start_date!)
      const end = exp.end_date ? new Date(exp.end_date) : today

      // 유효성 검사: 시작일이 종료일보다 늦으면 스왑
      if (start > end) {
        return { start: end, end: start }
      }

      return { start, end }
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime())

  // 2. 겹치는 구간 병합 (Interval Merging)
  const merged: ExperienceInterval[] = []
  let current = { ...sorted[0] }

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i]

    // 겹치는 경우: next.start <= current.end
    if (next.start <= current.end) {
      // 종료일을 더 늦은 날짜로 확장
      current.end = new Date(Math.max(current.end.getTime(), next.end.getTime()))
    } else {
      // 겹치지 않는 경우: 기존 구간 저장 후 다음 구간으로 이동
      merged.push(current)
      current = { ...next }
    }
  }
  // 마지막 구간 추가
  merged.push(current)

  // 3. 병합된 각 구간의 길이를 합산 (월 단위)
  const totalMonths = merged.reduce((sum, interval) => {
    const months =
      (interval.end.getFullYear() - interval.start.getFullYear()) * 12 +
      (interval.end.getMonth() - interval.start.getMonth())
    return sum + months
  }, 0)

  const years = Math.floor(totalMonths / 12)
  const months = totalMonths % 12

  return { years, months }
}
